import React, {useState} from "react";
import {Button, DialogContent, DialogTitle, Grid} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {api} from "../../../../api/API";
import {AutoComplete} from "../../shopdrawing/openings/AutoComplete";
import {DoorElevation} from "../../../../api/Openings";
import {useProjectId} from "../../shopdrawing/ProjectName";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {useFilePicker} from "../../shopdrawing/extras/FilePicker";
import {useAsyncAction} from "nate-react-api-helpers";
import useWindowSize from "react-use/lib/useWindowSize";
import {getAll} from "../../../logistics/routes/useGetAll";
import {ext} from "../../../../api/Files";
import {useIsQuote} from "../QuoteContainer";
import {useAlternative} from "../alternative/Alternative";

export type DoorElevationCompat = {
    doorElevation: string;
    doorElevationRenameOnlyFlag?: boolean;
    doorElevationFile?: number;
    doorElevationFileName?: string;
}

export function DoorElevationPopup<T extends DoorElevationCompat>(props: {
    row: T;
    requireNew?: boolean;
    onCancel(): void;
    onDone?(value: T): Promise<any>;
}) {

    const onDoneRef = useSyncedRef(props.onDone);
    const doneCb = useAsyncAction(async (input) => {
        if(!onDoneRef.current) return;
        await onDoneRef.current(input)
    }, []);
    const [row, setRow] = useState(Object.assign({}, props.row));

    const submit = () => {
        if(!props.onDone) return;
        doneCb.callback(Object.assign({}, props.row, {
            doorElevation: row.doorElevation,
            doorElevationFile: row.doorElevationFile,
            doorElevationRenameOnlyFlag: row.doorElevationRenameOnlyFlag,
        }))
    };

    return (
        <Dialog2 open onClose={props.onCancel} fullWidth maxWidth="lg" onEnterPress={submit}>
            <DialogTitle>Door Elevation</DialogTitle>
            <DialogContent>
                <DoorElevationInner
                    doorElevation={props.row.doorElevation}
                    doorElevationFile={props.row.doorElevationFile}
                    doorElevationFileName={props.row.doorElevationFileName}

                    onUpdate={(obj) => {
                        setRow(Object.assign({}, props.row, {
                            doorElevation: obj.doorElevation,
                            doorElevationFile: obj.doorElevationFile,
                            doorElevationRenameOnlyFlag: obj.doorElevationRenameOnlyFlag,
                        }))
                    }}
                    requireNew={props.requireNew}
                    readOnly={!props.onDone}
                />
            </DialogContent>
            <SmoothActionBar
                loading={doneCb.loading}
                error={doneCb.error}
                actionName="Done"
                onCancel={props.onCancel}
                onSubmit={props.onDone ? submit : undefined}
            />
        </Dialog2>
    )
}

export function DoorElevationInner(props: {
    doorElevation: string;
    doorElevationFile?: number;
    doorElevationFileName?: string;
    requireNew?: boolean;
    readOnly?: boolean;

    onUpdate(value: {
        doorElevation: string;
        doorElevationFile?: number;
        doorElevationFileName?: string;
        doorElevationRenameOnlyFlag?: boolean;
    }): void;
}) {
    const filePicker = useFilePicker({
        accept: ["application/pdf", "image/*"],
    });

    const [name, setName] = useState(props.doorElevation);
    const [file, setFile] = useState(props.doorElevationFile && props.doorElevationFile > 0 ? {name: props.doorElevationFileName, id: props.doorElevationFile} : null);
    const projectId = useProjectId();
    const isQuote = useIsQuote();
    const alt = useAlternative();

    const onUpdateRef = useSyncedRef(props.onUpdate)
    const upload = useAsyncAction(async (file: File) => {

        const fileObj = await api.files.upload({
            file: file,
            // use guid naming to prevent collisions
            name: window.crypto.randomUUID() + ext(file.name),
            convert2pdf: true,
        });

        setFile({
            id: fileObj.id,
            name: fileObj.name,
        });

        onUpdateRef.current({
            doorElevation: name,
            doorElevationFile: fileObj.id,
            doorElevationFileName: fileObj.name,
        })
    }, [filePicker, name]);

    const size = useWindowSize();
    const [isCreate, setIsCreate] = useState(false);
    const preventEditForRequireNew = props.requireNew && !isCreate;

    return (
        <>
            <div style={{height: 8}}/>
            <Grid container spacing={1}>
                <Grid item xs={8}>
                    {file && file.id > 0 ? <iframe
                        src={api.files.downloadLink(file.id, "inline")}
                        style={{
                            height: size.height * 0.75,
                            width: "100%",
                            border: "1px solid black",
                        }}
                    ></iframe> : <div>Choose a file on the right</div>}
                </Grid>
                <Grid item xs={4}>
                    <AutoComplete<DoorElevation>
                        fullWidth
                        label="Name"
                        size="small"
                        disabled={props.readOnly}
                        search={async input => {

                            let result = await getAll(null, offset =>
                                isQuote ? api.quoteOpenings.listDoorElevations({
                                    project: projectId,
                                    alternative: alt,
                                    offset: offset,
                                }): api.openings.listDoorElevations({
                                    project: projectId,
                                    offset: offset,
                                }))

                            if (input !== "") {
                                const value = input;
                                input = input.toLowerCase();
                                result = result.filter(r => r.name.toLowerCase().indexOf(input) !== -1)

                                if(file && isQuote && !props.requireNew) {
                                    if (!result.find(v => v.name.toLowerCase() === input.toLowerCase())) {
                                        result.push({
                                            id: Date.now(),
                                            name: "Update to '" + value + "' (no price change)",
                                            value: value,
                                            isUpdateName: true,
                                        } as any)
                                    }
                                }
                            }

                            return result;
                        }}
                        value={{name: name, fileId: file}}
                        equals={(a, b) => a.name === b.name}
                        makeLabel={a => a.name}
                        onSelect={value => {

                            setIsCreate(false);

                            if("isUpdateName" in value && value.isUpdateName) {
                                // @ts-ignore
                                setName(value.value);

                                props.onUpdate({
                                    // @ts-ignore
                                    doorElevation: value.value,
                                    doorElevationFile: file ? file.id : undefined,
                                    doorElevationFileName: file ? file.name : undefined,
                                    doorElevationRenameOnlyFlag: isQuote ? true : undefined,
                                })

                                return;
                            }

                            setFile(value.file ? {
                                id: value.file,
                                name: value.fileName || "",
                            } : null);

                            setName(value.name);

                            props.onUpdate({
                                doorElevation: value.name,
                                doorElevationFile: value.file,
                                doorElevationFileName: value.fileName,
                            })
                        }}
                        onCreate={async name => {
                            setName(name);
                            setFile(null);
                            setIsCreate(true);

                            props.onUpdate({
                                doorElevation: name,
                                doorElevationFile: undefined,
                                doorElevationFileName: undefined,
                            })
                        }}
                    />

                    <div style={{height: 24}}/>

                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs>
                            {file ? file.name : "No file selected"}
                        </Grid>
                        <Grid item>
                            <Button disabled={props.readOnly || preventEditForRequireNew} variant="outlined" size="small" onClick={async () => {
                                const files = await filePicker();
                                upload.callback(files[0])
                            }}>
                                Choose File
                            </Button>
                        </Grid>
                        {file && <Grid item>
                            <Button disabled={props.readOnly || preventEditForRequireNew} variant="outlined" size="small" onClick={() => {
                                setFile(null)
                                props.onUpdate({
                                    doorElevation: name,
                                    // -1 clears the file for all openings with this elevation
                                    doorElevationFile: -1,
                                    doorElevationFileName: undefined,
                                })
                            }}>
                                Clear File
                            </Button>
                        </Grid>}
                    </Grid>

                    {upload.LoadingElement}
                </Grid>
            </Grid>
        </>
    )
}